import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPageUrl, uploadFile } from "@/utils";
import { Product } from "@/entities/Product";
import { Creator } from "@/entities/Creator";
import { User } from "@/entities/User";
import { PlusCircle, Pencil, Trash2, X, Upload, Star, AlertCircle, ArrowLeft, ImagePlus, Check } from "lucide-react";

const CATEGORIES = ["Artist (Entertainment)", "Content Creator", "Craftsman (Build To Spec)", "Healer (Remedies; Protocol)", "Manifestor", "Motivator", "Soul Worker (Passion Service)", "Problem Solver", "Visionary"];
const EMPTY_FORM = { name: "", description: "", price: "", original_price: "", category: "Art", creator_id: "", creator_name: "", stock: 10, status: "active", is_featured: false, is_new: true, tags: "", images: [] };

function ProductForm({ product, creators, onSave, onClose }) {
  const [form, setForm] = useState(product ? { ...product, tags: (product.tags || []).join(", ") } : EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleCreator = (id) => {
    const c = creators.find(c => c.id === id);
    setForm(f => ({ ...f, creator_id: id, creator_name: c?.name || "" }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const { url } = await uploadFile(file, { isPublic: true, onProgress: setUploadProgress });
    setForm(f => ({ ...f, images: [...(f.images || []), url] }));
    setUploading(false);
    setUploadProgress(0);
  };

  const removeImage = (idx) => setForm(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const data = {
      ...form,
      price: parseFloat(form.price),
      original_price: form.original_price ? parseFloat(form.original_price) : null,
      stock: parseInt(form.stock),
      tags: form.tags ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
    };
    if (product?.id) await Product.update(product.id, data);
    else await Product.create(data);
    setSaving(false);
    onSave();
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-[#e8e4de] px-6 py-4 flex items-center justify-between z-10">
          <h2 className="font-display text-xl font-bold text-[#1a1a1a]">{product ? "Edit Product" : "Add Product"}</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400 hover:text-[#1a1a1a]" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Images */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Product Images</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {(form.images || []).map((img, i) => (
                <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-[#e8e4de]">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeImage(i)} className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                    <X className="w-2.5 h-2.5 text-white" />
                  </button>
                </div>
              ))}
              <label className="w-16 h-16 border-2 border-dashed border-[#e8e4de] rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#c8a96e] transition-colors">
                {uploading ? <div className="text-[10px] text-[#c8a96e] font-medium">{uploadProgress}%</div> : <ImagePlus className="w-5 h-5 text-gray-400" />}
                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
              </label>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Product Name *</label>
            <input required value={form.name} onChange={e => set("name", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="e.g. Handmade Ceramic Mug" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Description *</label>
            <textarea required value={form.description} onChange={e => set("description", e.target.value)} rows={3} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e] resize-none" placeholder="Describe the product..." />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Price ($) *</label>
              <input required type="number" min="0" step="0.01" value={form.price} onChange={e => set("price", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="0.00" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Original Price ($)</label>
              <input type="number" min="0" step="0.01" value={form.original_price || ""} onChange={e => set("original_price", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="Optional" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Category *</label>
              <select value={form.category} onChange={e => set("category", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e] bg-white">
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Stock</label>
              <input type="number" min="0" value={form.stock} onChange={e => set("stock", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Creator *</label>
            <select required value={form.creator_id} onChange={e => handleCreator(e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e] bg-white">
              <option value="">Select a creator...</option>
              {creators.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Tags (comma separated)</label>
            <input value={form.tags} onChange={e => set("tags", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="handmade, ceramic, gift" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Status</label>
            <select value={form.status} onChange={e => set("status", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e] bg-white">
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="sold_out">Sold Out</option>
            </select>
          </div>

          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_featured} onChange={e => set("is_featured", e.target.checked)} className="w-4 h-4 accent-[#c8a96e]" />
              <span className="text-sm text-gray-700">Featured on Homepage</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_new} onChange={e => set("is_new", e.target.checked)} className="w-4 h-4 accent-[#c8a96e]" />
              <span className="text-sm text-gray-700">Mark as New</span>
            </label>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-[#e8e4de] text-[#1a1a1a] py-2.5 rounded-full text-sm font-medium hover:bg-[#f5f3ef] transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 bg-[#1a1a1a] hover:bg-[#c8a96e] text-white py-2.5 rounded-full text-sm font-semibold transition-colors flex items-center justify-center gap-2">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check className="w-4 h-4" />}
              {saving ? "Saving..." : product ? "Save Changes" : "Add Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ProductManager() {
  const [products, setProducts] = useState([]);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [search, setSearch] = useState("");
  const [unauthorized, setUnauthorized] = useState(false);

  useEffect(() => {
    async function load() {
      const me = await User.me();
      if (me?.role !== "admin") { setUnauthorized(true); setLoading(false); return; }
      const [prods, crts] = await Promise.all([Product.list("-createdAt", 100), Creator.list("-createdAt", 100)]);
      setProducts(prods);
      setCreators(crts);
      setLoading(false);
    }
    load();
  }, []);

  const refresh = async () => {
    const prods = await Product.list("-createdAt", 100);
    setProducts(prods);
    setFormOpen(false);
    setEditProduct(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await Product.delete(id);
    setProducts(p => p.filter(x => x.id !== id));
  };

  const filtered = products.filter(p =>
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.creator_name?.toLowerCase().includes(search.toLowerCase()) ||
    p.category?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin w-8 h-8 border-4 border-[#c8a96e] border-t-transparent rounded-full" /></div>;
  if (unauthorized) return <div className="flex flex-col items-center justify-center min-h-[60vh] text-center"><AlertCircle className="w-12 h-12 text-red-400 mb-4" /><h2 className="font-display text-2xl font-bold mb-2">Admin Access Only</h2></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center gap-3 mb-2">
        <Link to={createPageUrl("Admin")} className="text-gray-400 hover:text-[#c8a96e] transition-colors"><ArrowLeft className="w-4 h-4" /></Link>
        <p className="text-[#c8a96e] text-xs font-semibold uppercase tracking-widest">Admin · Products</p>
      </div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl font-bold text-[#1a1a1a]">Product Manager</h1>
        <button onClick={() => { setEditProduct(null); setFormOpen(true); }} className="inline-flex items-center gap-2 bg-[#1a1a1a] hover:bg-[#c8a96e] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors">
          <PlusCircle className="w-4 h-4" /> Add Product
        </button>
      </div>

      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products, creators, categories..." className="w-full border border-[#e8e4de] rounded-xl px-4 py-2.5 text-sm mb-6 focus:outline-none focus:border-[#c8a96e]" />

      <div className="bg-white border border-[#e8e4de] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#faf9f7] border-b border-[#e8e4de]">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Product</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Creator</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Category</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Price</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Stock</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e8e4de]">
              {filtered.map(p => (
                <tr key={p.id} className="hover:bg-[#faf9f7] transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg overflow-hidden bg-[#f5f3ef] flex-shrink-0">
                        <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="font-medium text-[#1a1a1a] line-clamp-1">{p.name}</p>
                        {p.is_featured && <span className="text-[10px] text-[#c8a96e] font-semibold">★ Featured</span>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{p.creator_name}</td>
                  <td className="px-4 py-3 text-gray-500">{p.category}</td>
                  <td className="px-4 py-3 font-semibold text-[#1a1a1a]">${p.price}</td>
                  <td className="px-4 py-3 text-gray-500">{p.stock}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${p.status === "active" ? "bg-green-100 text-green-600" : p.status === "draft" ? "bg-yellow-100 text-yellow-600" : "bg-red-100 text-red-500"}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => { setEditProduct(p); setFormOpen(true); }} className="p-1.5 text-gray-400 hover:text-[#c8a96e] transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelete(p.id)} className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-gray-400 text-sm">No products found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {formOpen && <ProductForm product={editProduct} creators={creators} onSave={refresh} onClose={() => { setFormOpen(false); setEditProduct(null); }} />}
    </div>
  );
}